import { useEffect, useState } from "react";
import "./ComplianceModal.css";
import { Link, useNavigate } from "react-router-dom";
import { requestDeleteCompliance, requestUpdateCompliance } from "../../services/wisdomServices/complianceService";
import { extractAndFormatDate } from "../../utils/helper/extractAndFormatDate";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from 'primereact/dropdown';
import { getSessionItem } from '../../utils/helper/session'
import { Can, PERMISSIONS } from "../../security";

/**
 * @function ComplianceModal
 * @param {Object} props 
 * @param {Object} props.compliance 
 * @param {Function} props.onCloseModal 
 * @param {Function} props.onCloseModalByClick 
 * @param {Function} props.onCloseAfterSaving 
 * @returns {JSX.Element} - compliance review modal
 */
function ComplianceModal({ compliance, onCloseModal, onCloseModalByClick, onCloseAfterSaving, open=false }) {
  const navigate = useNavigate();
  const normalizeBoolean = (value) => {
    return typeof value === "boolean"
      ? value
      : value === 1 || value === "1";
  };

  const [disabled, setDisabled] = useState(true);
  const [reviewedWithAgent, setReviewedWithAgent] = useState(normalizeBoolean(compliance.reviewedWithAgent));
  const [isOpen, setIsOpen] = useState(false);
  const [agentDiscussionSummary, setAgentDiscussionSummary] = useState(compliance.agentDiscussionSummary || "");
  const [closureReason, setClosureReason] = useState(compliance.closureReason || "");
  const [loadingDelete, setLoadingDelete] = useState(false);
  const formattedDate = extractAndFormatDate(compliance.detail);
  const userId = compliance.userID ?? compliance.userId ?? compliance.user?.ID;
  const user = getSessionItem("User");
  const isAdmin = user.roleName == "VP"

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCloseModal();
      }
    };

    const handleClickOutside = (event) => {
      if (event.target.classList.contains("compliance-modal")) {
        onCloseModalByClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("click", handleClickOutside);
    };

  }, [onCloseModal, onCloseModalByClick]);
  
  useEffect(() => {
    const isDisabled =
    agentDiscussionSummary.trim() === "" || closureReason.trim() === "";
    setDisabled(isDisabled);
  }, [agentDiscussionSummary, closureReason])

  useEffect(() => {
    setReviewedWithAgent(compliance.reviewedWithAgent ?? false);
    setIsOpen(normalizeBoolean(compliance.isOpen));
    setAgentDiscussionSummary(compliance.agentDiscussionSummary || "");
    setClosureReason(compliance.closureReason || "");
  }, [compliance]);



  const complianceReviewedOptions = [
    {value:false, label:"No"},
    {value:true, label:"Yes"}
  ]

  const complianceStatus = [
    {value:true, label:"Open"},
    {value:false, label:"Closed"}
  ]

  function handleCloseModal() {
    onCloseModalByClick();
  }

  function handleClosureReason(e) {
    const value = e.target.value;
    setClosureReason(value);
  }

  /**
   * @function handleAgentDiscussion
   * @param {Event} e 
   * @description - handles updates to the agent discussion summary field
   */
  function handleAgentDiscussion(e) {
    const value = e.target.value;
    setAgentDiscussionSummary(value);
  }

  /**
   * @function handleReviewedWithAgentStatus
   * @param {Event} e 
   * @description - updates the "reviewed with agent" status
   */
  function handleReviewedWithAgentStatus(e) {
    setReviewedWithAgent(e.value);
  }

  /**
   * @function handleIsOpenStatus
   * @param {Event} e 
   * @description - updates the compliance open/closed status
   */
  function handleIsOpenStatus(e) {
    setIsOpen(e.value)
  }

  const handleNavigateToUserDiary = () => {
    navigate(`/manager/diary/${userId}`);
  }

  /**
   * @async
   * @function handleComplianceUpdate
   * @param {Event} event 
   * @description - submits compliance updates to the API
   */
  async function handleComplianceUpdate(event) {
    event.preventDefault();

    const keys = {
      reviewedWithAgent,
      agentDiscussionSummary: agentDiscussionSummary?.trim() || "",
      closureReason: closureReason?.trim() || "",
      isOpen
    };
    try {
      await requestUpdateCompliance(compliance.id, keys)
      onCloseAfterSaving();
      
    } catch (error) {
      console.error(error)
    }
  }

  async function handleDeleteCompliance(event){
    event.preventDefault();
    try {
      setLoadingDelete(true)
      await requestDeleteCompliance(compliance.id)
      onCloseAfterSaving();
    } catch (error) {
      console.error("Error deleting compliance: ", error)

    } finally {
      setLoadingDelete(false)
    }
  }

  const dialogFooter = (
    <div className="compliance-modal-footer-btns">
      <div>
        {isAdmin && (
          <Can permission={PERMISSIONS.complianceById_delete}>
            <Button label="Delete" icon="pi pi-trash" onClick={handleDeleteCompliance} severity="danger" loading={loadingDelete}/>
          </Can>
        
        )}

      </div>
      <div className="compliance-modal-right-footer-btns">
        <Can permission={PERMISSIONS.complianceById_put}>
          <Button label="Save" icon="pi pi-check" onClick={handleComplianceUpdate} autoFocus disabled={disabled} severity="success"/>
        </Can>
        <Button label="Cancel" icon="pi pi-times" onClick={handleCloseModal} severity="warning"/>
      </div>
    </div>
  )
  const dialogHeader = (
    <div className="compliance-dialog-header">
      <span>
        Compliance Item
        </span>
        {formattedDate && userId && (
          <Link className="navigate-url" to={`/manager/diary/${userId}`} state={{ date: formattedDate }}>Go to user diary</Link>
        )}
    </div>
  )


  
  return (
    <Dialog
      dismissableMask
      visible={open}
      onHide={handleCloseModal}
      footer={dialogFooter}
      header={dialogHeader}
      style={{width:"70%"}}
    >
      <div className="p-2 mt-2 mb-2 card flex flex-row md:flex-row gap-3">
          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-id">Compliance #</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-hashtag"></i>
              </span>
              <InputText
                id="form-compliance-id"
                value={compliance.id}
                disabled
                readOnly
              />
            </div>
                    
          </div>

          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-user">User</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-user"></i>
              </span>
              <InputText
                id="form-compliance-user"
                value={compliance.user}
                disabled
                readOnly
              />
            </div>
                    
          </div>
      </div>
      
      <div className="p-2 mt-2 mb-2 card flex flex-row md:flex-row gap-3">
          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-manager">Manager</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-user-plus"></i>
              </span>
              <InputText
                id="form-compliance-manager"
                value={compliance.manager}
                disabled
                readOnly
              />
            </div>
                    
          </div>

          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-project">Project</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-desktop"></i>
              </span>
              <InputText
                id="form-compliance-project"
                value={compliance.project}
                disabled
                readOnly
              />
            </div>
                    
          </div>
      </div>

      <div className="p-2 mt-2 mb-2 card flex flex-row md:flex-row gap-3">
          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-type">Compliance Type</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-clipboard"></i>
              </span>
              <InputText
                id="form-compliance-type"
                value={compliance.complianceType}
                disabled
                readOnly
              />
            </div>
                    
          </div>

          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-details">Detail</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-info-circle"></i>
              </span>
              <InputTextarea
                autoResize
                value={compliance.detail}
                id="form-compliance-detail"
                disabled
                readOnly
              />
            </div>
                    
          </div>
      </div>

      <div className="p-2 mt-2 mb-2 card flex flex-row md:flex-row gap-3">
          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-reviewed">Reviewed with agent</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-file-edit"></i>
              </span>
              <Dropdown
                id="form-compliance-reviewed"
                value={reviewedWithAgent}
                options={complianceReviewedOptions}
                optionLabel="label"
                optionValue="value"
                onChange={handleReviewedWithAgentStatus}
              />
            </div>
                    
          </div>

          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-status">Open</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className={isOpen ? "pi pi-lock-open" : "pi pi-lock"}></i>
              </span>
              <Dropdown
                id="form-compliance-status"
                value={isOpen}
                options={complianceStatus}
                optionLabel="label"
                optionValue="value"
                onChange={handleIsOpenStatus}
              />
            </div>
                    
          </div>

          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-createdAt">Created At</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-clock"></i>
              </span>
              <InputText
                id="form-compliance-createdAt"
                value={compliance.createDate}
                disabled
                readOnly
              />
            </div>
                    
          </div>
      </div>

      <div className="p-2 mt-2 mb-2 card flex flex-row md:flex-row gap-3">
          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-discussion">Agent discussion summary (max. 250)</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-comments"></i>
              </span>
              <InputTextarea
                autoResize
                id="form-compliance-discussion"
                value={agentDiscussionSummary}
                onChange={handleAgentDiscussion}
                maxLength={250}
              />
            </div>
                    
          </div>

          <div className="p-inputgroup flex-column flex-1">
            <label htmlFor="form-compliance-closure-reason">Closure reason (max. 50)</label>

            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-pen-to-square"></i>
              </span>
              <InputTextarea
                autoResize
                id="form-compliance-closure-reason"
                value={closureReason}
                onChange={handleClosureReason}
                maxLength={50}
                
              />
            </div>
                    
          </div>
      </div>
      
      
    </Dialog>
  );
}

export default ComplianceModal;
